import { useState } from "react";
import { userApi } from "../api/user";
import { UpdatePasswordPayload } from "../types/user";

/**
 * Custom React hook to handle updating the user's password.
 *
 * Provides a status object for loading, error, and success states,
 * a function to clear the status, and a function to send the update request.
 *
 * @returns {{
 *   status: { loading: boolean; error: string | null; success: string | null },
 *   clearStatus: () => void,
 *   send: (data: UpdatePasswordPayload) => Promise<void>
 * }}
 */
export function usePasswordUpdate() {
  // State to track the status of the update operation
  const [status, setStatus] = useState<{
    loading: boolean;
    error: string | null;
    success: string | null;
  }>({ loading: false, error: null, success: null });

  /**
   * Resets the status state to its initial values.
   */
  const clearStatus = () =>
    setStatus({ loading: false, error: null, success: null });

  /**
   * Sends the update password request to the API.
   *
   * @param {UpdatePasswordPayload} data - The new password data.
   * @returns {Promise<void>} Resolves if the update is successful.
   * @throws Will throw an error if the update fails.
   */
  const send = async (data: UpdatePasswordPayload) => {
    setStatus({ loading: true, error: null, success: null });
    const fd = new FormData();
    // Append password and password confirmation to the FormData
    fd.append("password", data.password);
    fd.append("password_confirmation", data.password_confirmation);
    try {
      // Call the API to update the password
      await userApi.updatePassword(fd);
      setStatus({
        loading: false,
        error: null,
        success: "Password updated successfully!",
      });
    } catch (e: any) {
      // Set error status if the API call fails
      setStatus({
        loading: false,
        error: e?.message || "Failed to update password",
        success: null,
      });
      throw e;
    }
  };

  // Return the status, clearStatus, and send function for use in components
  return { status, clearStatus, send };
}
